import React, { useEffect, useRef, useState } from 'react';
import { useProject } from '../../hooks/useProject';
import { getConfigService, getTokenService } from '../../services/angular-bridge';
import { SubmitButton } from '../../components/SubmitButton';
import { formatBytes } from '../../utilities/format';

export function MetadataTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [draggingOver, setDraggingOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bytesLoaded, setBytesLoaded] = useState(0);
  const [bytesTotal, setBytesTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Abort an in-flight upload if the user navigates away, so the load/error
  // handlers don't fire setState on an unmounted component.
  useEffect(() => () => xhrRef.current?.abort(), []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(true);
  };
  const handleDragLeave = () => setDraggingOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f) validateAndSetFile(f);
  };

  const validateAndSetFile = (f: File) => {
    if (f.size <= 0) {
      setError('Zero byte file not allowed.');
      return;
    }
    if (f.size > 2147483648) {
      setError('Please use our SFTP service to upload anything greater than 2 GB. Your Project SFTP credentials were emailed to you when the project was created.');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    const url = `${getConfigService().getServerBaseUrl()}/project/${projectId}/loadFile`;
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.upload.addEventListener('progress', ev => {
      if (ev.lengthComputable) {
        setBytesLoaded(ev.loaded);
        setBytesTotal(ev.total);
        setProgress(Math.floor((ev.loaded / ev.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setSuccess(`Load file import started — ${file.name}`);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setError('Error uploading load file. Please try again.');
      }
    });
    xhr.addEventListener('error', () => {
      setUploading(false);
      setError('Error uploading load file. Please try again.');
    });
    xhr.open('POST', url);
    const token = getTokenService().GetAccessToken?.();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  };

  return (
    <div>
      <h2><i className="fas fa-share" aria-hidden="true" /> Project User Metadata Update</h2>

      <div className="row">
        <div className="col-md-6">
          <form onSubmit={handleSubmit} noValidate>
            <p className="lead">
              <i className="fas fa-cloud-upload-alt" aria-hidden="true" /> Update user metadata for {project?.name}
            </p>

            <div className="form-group">
              <div
                className={`well text-center${draggingOver ? ' well-sm' : ''}`}
                style={{ cursor: 'pointer', border: draggingOver ? '2px dashed #337ab7' : undefined }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-file-upload fa-2x" aria-hidden="true" />
                <p style={{ marginTop: 8 }}>
                  {file ? file.name : 'Drop a file here or click to browse'}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div>
              <SubmitButton
                className="btn btn-primary btn-lg"
                pending={uploading}
                pendingLabel="Uploading…"
                disabled={!file}
              >
                <i className="fas fa-cogs" aria-hidden="true" /> Upload and Import
              </SubmitButton>
            </div>

            {uploading && (
              <div className="alert alert-block alert-info" style={{ marginTop: 20 }}>
                <h4>Uploading...</h4>
                <p style={{ marginTop: 10 }}>
                  {formatBytes(bytesLoaded)} of {formatBytes(bytesTotal)}
                  <span className="label label-info pull-right">{progress}%</span>
                </p>
                <div className="progress progress-striped">
                  <div className="bar" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="col-md-6">
          <h4>Example Load File: <small>CSV Format</small></h4>
          <code>
            "id", "Folder Name", "Depo Exibit"<br />
            "54f79d177cea7b2936416594", "123 Folder", "DEP-001"<br />
            "54f79d177cea7b2936416595", "456 Folder", "DEP-002"
          </code>
        </div>
      </div>
    </div>
  );
}
